import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchLabels } from '../utils/labelApi';
import { CreateLabelModal, LabelManager } from './labels';
import './HubPages.css';

/**
 * Labels hub — manage tags and open labeled mail.
 * Opened from Workspace → Labels (/labels).
 */
const LabelsPage = () => {
  const navigate = useNavigate();
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const loadLabels = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetchLabels(true);
      if (response.success) {
        setLabels(response.data || []);
      } else {
        setError(response.message || 'Failed to load labels');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error loading labels');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLabels();
    const refresh = () => loadLabels();
    window.addEventListener('labelsUpdated', refresh);
    return () => window.removeEventListener('labelsUpdated', refresh);
  }, [loadLabels]);

  return (
    <div className="hub-page">
      <div className="hub-header">
        <div>
          <h1>Labels</h1>
          <p>Organize mail with tags</p>
        </div>
        <button type="button" className="hub-primary-btn" onClick={() => setShowCreate(true)}>
          + New label
        </button>
      </div>

      <div className="hub-body">
        {loading && <div className="hub-state">Loading labels…</div>}
        {!loading && error && <div className="hub-state hub-error">{error}</div>}
        {!loading && !error && (
          <>
            <LabelManager labels={labels} onChange={setLabels} />
            {labels.length > 0 && (
              <div className="hub-quick-open">
                <h2>Open label</h2>
                <div className="hub-chip-links">
                  {labels
                    .filter((label) => label.visible !== false)
                    .map((label) => (
                      <button
                        key={label._id}
                        type="button"
                        className="hub-chip-link"
                        style={{ '--label-color': label.color || '#1a73e8' }}
                        onClick={() => navigate(`/labels/${label._id}`)}
                      >
                        {label.name}
                        {label.count ? ` · ${label.count}` : ''}
                      </button>
                    ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <CreateLabelModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(created) => {
          setLabels((prev) => [...prev, created]);
          window.dispatchEvent(new CustomEvent('labelsUpdated'));
          setShowCreate(false);
        }}
      />
    </div>
  );
};

export default LabelsPage;
